import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, BedIcon, BathIcon, SquareIcon, CalendarIcon, UserIcon, PhoneIcon, MailIcon, CheckCircleIcon, BuildingIcon, StarIcon } from 'lucide-react';
import ImageGallery from '../components/ImageGallery';
import ScheduleVisitForm from '../components/ScheduleVisitForm';
import DocumentList from '../components/DocumentList';
import CommentsSection from '../components/CommentsSection';
import { mockListings, formatCurrency, formatArea, formatDate } from '../utils/mockData';
const ListingDetailPage = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [listing, setListing] = useState(mockListings[0]);
  const [visitRequested, setVisitRequested] = useState(false);
  useEffect(() => {
    // Simulate API fetch
    const foundListing = mockListings.find(item => item.id === id);
    if (foundListing) {
      setListing(foundListing);
    }
  }, [id]);
  const handleAddComment = (rating: number, comment: string) => {
    const newComment = {
      id: `c${Date.now()}`,
      userId: currentUser.id,
      userName: currentUser.name,
      rating,
      comment,
      createdAt: new Date().toISOString()
    };
    const updatedComments = [...listing.comments, newComment];
    const newTotalRatings = listing.totalRatings + 1;
    const newAverageRating = Math.round(updatedComments.reduce((sum, c) => sum + c.rating, 0) / updatedComments.length * 10) / 10;
    setListing({
      ...listing,
      comments: updatedComments,
      totalRatings: newTotalRatings,
      averageRating: newAverageRating
    });
  };
  const handleScheduleVisit = (formData: any) => {
    console.log('Visit scheduled:', formData);
    setVisitRequested(true);
  };
  if (!listing) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-815">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-816">Property not found</h2>
        <p className="mt-2 text-gray-600" data-id="element-817">
          The property you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/browse" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-md" data-id="element-818">
          Browse Properties
        </Link>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-819">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-820">
        {/* Breadcrumb */}
        <nav className="mb-6" data-id="element-821">
          <ol className="flex text-sm" data-id="element-822">
            <li className="flex items-center" data-id="element-823">
              <Link to="/" className="text-blue-600 hover:text-blue-800" data-id="element-824">
                Home
              </Link>
              <span className="mx-2 text-gray-500" data-id="element-825">/</span>
            </li>
            <li className="flex items-center" data-id="element-826">
              <Link to="/browse" className="text-blue-600 hover:text-blue-800" data-id="element-827">
                Browse
              </Link>
              <span className="mx-2 text-gray-500" data-id="element-828">/</span>
            </li>
            <li className="text-gray-600 truncate" data-id="element-829">{listing.title}</li>
          </ol>
        </nav>
        {/* Listing Header */}
        <div className="mb-8" data-id="element-830">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2" data-id="element-831">
            <div className="flex items-center" data-id="element-832">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-833">
                {listing.title}
              </h1>
              <span className={`ml-3 px-3 py-1 rounded-full text-sm font-medium ${listing.listingType === 'rent' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-834">
                For {listing.listingType === 'rent' ? 'Rent' : 'Sale'}
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-600 mt-2 md:mt-0" data-id="element-835">
              {formatCurrency(listing.price)}
              {listing.listingType === 'rent' && <span className="text-base text-gray-600" data-id="element-836">
                  /{listing.rentPeriod === 'monthly' ? 'month' : 'year'}
                </span>}
            </p>
          </div>
          <div className="flex items-center text-gray-600" data-id="element-837">
            <MapPinIcon className="h-5 w-5 mr-1" data-id="element-838" />
            <p data-id="element-839">{listing.location}</p>
          </div>
          {listing.totalRatings > 0 && <div className="flex items-center mt-2" data-id="element-840">
              <StarIcon className="h-5 w-5 text-yellow-500 fill-yellow-500 mr-1" data-id="element-841" />
              <span className="text-lg font-medium text-gray-900" data-id="element-842">
                {listing.averageRating}
              </span>
              <span className="text-sm text-gray-500 ml-2" data-id="element-843">
                ({listing.totalRatings}{' '}
                {listing.totalRatings === 1 ? 'review' : 'reviews'})
              </span>
            </div>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-844">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-845">
            <ImageGallery images={listing.images} data-id="element-846" />
            <div className="bg-white rounded-lg shadow-md p-6" data-id="element-847">
              <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-848">
                Property Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-849">
                <div className="bg-gray-50 p-4 rounded-lg" data-id="element-850">
                  <p className="text-sm text-gray-500" data-id="element-851">Property Type</p>
                  <p className="font-medium text-gray-900 capitalize" data-id="element-852">
                    {listing.type}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg" data-id="element-853">
                  <p className="text-sm text-gray-500" data-id="element-854">Area</p>
                  <p className="font-medium text-gray-900" data-id="element-855">
                    {formatArea(listing.size)}
                  </p>
                </div>
                {listing.type === 'house' && <>
                    <div className="bg-gray-50 p-4 rounded-lg" data-id="element-856">
                      <p className="text-sm text-gray-500" data-id="element-857">Bedrooms</p>
                      <div className="flex items-center" data-id="element-858">
                        <BedIcon className="h-4 w-4 mr-1 text-gray-600" data-id="element-859" />
                        <p className="font-medium text-gray-900" data-id="element-860">
                          {listing.bedrooms}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg" data-id="element-861">
                      <p className="text-sm text-gray-500" data-id="element-862">Bathrooms</p>
                      <div className="flex items-center" data-id="element-863">
                        <BathIcon className="h-4 w-4 mr-1 text-gray-600" data-id="element-864" />
                        <p className="font-medium text-gray-900" data-id="element-865">
                          {listing.bathrooms}
                        </p>
                      </div>
                    </div>
                  </>}
              </div>
              <div className="mb-6" data-id="element-866">
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-867">
                  Description
                </h3>
                <p className="text-gray-700 whitespace-pre-line" data-id="element-868">
                  {listing.description}
                </p>
              </div>
              <div data-id="element-869">
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-870">
                  Listed
                </h3>
                <div className="flex items-center text-gray-700" data-id="element-871">
                  <CalendarIcon className="h-5 w-5 mr-2" data-id="element-872" />
                  <span data-id="element-873">{formatDate(listing.createdAt)}</span>
                </div>
              </div>
            </div>
            <DocumentList documents={listing.documents} data-id="element-874" />
            {/* Comments Section */}
            <CommentsSection comments={listing.comments} onAddComment={handleAddComment} data-id="element-875" />
          </div>
          {/* Sidebar */}
          <div className="space-y-6" data-id="element-876">
            {/* Agency/Owner Info */}
            <div className="bg-white rounded-lg shadow-md p-6" data-id="element-877">
              <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-878">
                {listing.owner.companyName ? 'Agency Information' : 'Owner Information'}
              </h3>
              {listing.owner.companyLogo && <div className="mb-4 flex justify-center" data-id="element-879">
                  <img src={listing.owner.companyLogo} alt={listing.owner.companyName} className="h-20 w-20 object-contain rounded-lg" data-id="element-880" />
                </div>}
              <div className="space-y-4" data-id="element-881">
                {listing.owner.companyName && <>
                    <div className="flex items-center" data-id="element-882">
                      <BuildingIcon className="h-5 w-5 text-gray-600 mr-3" data-id="element-883" />
                      <div data-id="element-884">
                        <p className="text-sm text-gray-500" data-id="element-885">Company</p>
                        <p className="font-medium text-gray-900" data-id="element-886">
                          {listing.owner.companyName}
                        </p>
                      </div>
                    </div>
                    {listing.owner.companyNIF && <div className="flex items-center" data-id="element-887">
                        <div className="h-5 w-5 mr-3 flex items-center justify-center" data-id="element-888">
                          <span className="text-xs font-bold text-gray-600" data-id="element-889">
                            NIF
                          </span>
                        </div>
                        <div data-id="element-890">
                          <p className="text-sm text-gray-500" data-id="element-891">Tax ID</p>
                          <p className="font-medium text-gray-900" data-id="element-892">
                            {listing.owner.companyNIF}
                          </p>
                        </div>
                      </div>}
                  </>}
                <div className="flex items-center" data-id="element-893">
                  <UserIcon className="h-5 w-5 text-gray-600 mr-3" data-id="element-894" />
                  <div data-id="element-895">
                    <p className="text-sm text-gray-500" data-id="element-896">Contact Person</p>
                    <p className="font-medium text-gray-900" data-id="element-897">
                      {listing.owner.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-898">
                  <PhoneIcon className="h-5 w-5 text-gray-600 mr-3" data-id="element-899" />
                  <div data-id="element-900">
                    <p className="text-sm text-gray-500" data-id="element-901">Phone</p>
                    <p className="font-medium text-gray-900" data-id="element-902">
                      {listing.owner.phone}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-903">
                  <MailIcon className="h-5 w-5 text-gray-600 mr-3" data-id="element-904" />
                  <div data-id="element-905">
                    <p className="text-sm text-gray-500" data-id="element-906">Email</p>
                    <p className="font-medium text-gray-900" data-id="element-907">
                      {listing.owner.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Schedule Visit Form */}
            {visitRequested ? <div className="bg-white rounded-lg shadow-md p-6" data-id="element-908">
                <div className="text-center py-4" data-id="element-909">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" data-id="element-910" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-911">
                    Visit Request Sent!
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-912">
                    The property owner will contact you shortly to confirm your
                    visit.
                  </p>
                  <Link to="/browse" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-913">
                    Browse More Properties
                  </Link>
                </div>
              </div> : <ScheduleVisitForm listingId={listing.id} onSubmit={handleScheduleVisit} data-id="element-914" />}
          </div>
        </div>
      </div>
    </div>;
};
export default ListingDetailPage;